import React from 'react';
import PolicyLayout from './PolicyLayout';

const CookiePolicy = () => {
  return (
    <PolicyLayout title="Cookie Policy" lastUpdated="January 20, 2026">
      <p>
        This Cookie Policy explains how AppealBot uses cookies and similar tracking technologies
        when you visit our platform. By using AppealBot, you consent to the use of cookies as
        described in this policy.
      </p>

      <h2>1. What Are Cookies?</h2>
      <p>
        Cookies are small text files stored on your device when you visit a website. They help
        websites remember information about your visit, making your next visit easier and the
        site more useful to you.
      </p>

      <h2>2. Types of Cookies We Use</h2>

      <h3>2.1 Essential Cookies</h3>
      <p>
        These cookies are necessary for the platform to function properly. They enable core
        functionality such as:
      </p>
      <ul>
        <li>User authentication and session management</li>
        <li>Security features and fraud prevention</li>
        <li>Load balancing and server optimization</li>
        <li>Remembering your preferences and settings</li>
      </ul>
      <p>
        <strong>Duration:</strong> Session or up to 1 year<br />
        <strong>Can be disabled:</strong> No (required for platform functionality)
      </p>

      <h3>2.2 Analytics Cookies</h3>
      <p>
        These cookies help us understand how visitors interact with our platform by collecting
        information anonymously:
      </p>
      <ul>
        <li>Pages visited and time spent on each page</li>
        <li>Features used and interaction patterns</li>
        <li>Error messages and performance metrics</li>
        <li>Traffic sources and navigation paths</li>
      </ul>
      <p>
        <strong>Duration:</strong> Up to 2 years<br />
        <strong>Can be disabled:</strong> Yes
      </p>

      <h3>2.3 Functional Cookies</h3>
      <p>
        These cookies enhance your experience by remembering choices you make:
      </p>
      <ul>
        <li>Language and regional preferences</li>
        <li>Display settings and layout preferences</li>
        <li>Recently viewed items and search history</li>
        <li>Form field auto-completion</li>
      </ul>
      <p>
        <strong>Duration:</strong> Up to 1 year<br />
        <strong>Can be disabled:</strong> Yes
      </p>

      <h2>3. Third-Party Cookies</h2>
      <p>
        We may use cookies from trusted third-party services:
      </p>
      <ul>
        <li><strong>Google Analytics:</strong> Website analytics and usage statistics</li>
        <li><strong>Stripe:</strong> Payment processing and fraud detection</li>
        <li><strong>Intercom/Support tools:</strong> Customer support chat functionality</li>
      </ul>
      <p>
        These third parties have their own privacy policies governing the use of their cookies.
      </p>

      <h2>4. Similar Technologies</h2>
      <p>
        In addition to cookies, we may use similar technologies:
      </p>
      <ul>
        <li><strong>Local Storage:</strong> Stores data locally in your browser for faster performance</li>
        <li><strong>Session Storage:</strong> Temporary storage that clears when you close your browser</li>
        <li><strong>Pixel Tags:</strong> Small images that help us track email opens and website visits</li>
      </ul>

      <h2>5. Cookie Management</h2>
      <p>
        You can control cookies through several methods:
      </p>

      <h3>5.1 Browser Settings</h3>
      <p>
        Most browsers allow you to manage cookie preferences. You can typically:
      </p>
      <ul>
        <li>View cookies stored on your device</li>
        <li>Delete all or specific cookies</li>
        <li>Block cookies from all or specific websites</li>
        <li>Set preferences for different cookie types</li>
      </ul>

      <h3>5.2 Platform Settings</h3>
      <p>
        When available, you can manage cookie preferences through our platform's privacy settings.
      </p>

      <h3>5.3 Opt-Out Links</h3>
      <ul>
        <li>Google Analytics: <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">https://tools.google.com/dlpage/gaoptout</a></li>
      </ul>

      <h2>6. Impact of Disabling Cookies</h2>
      <p>
        If you disable or delete cookies:
      </p>
      <ul>
        <li>Some features may not work properly</li>
        <li>You may need to log in more frequently</li>
        <li>Your preferences may not be saved</li>
        <li>You may see less relevant content</li>
      </ul>

      <h2>7. Cookie List</h2>
      <p>
        Below is a detailed list of cookies we use:
      </p>

      <h3>Essential Cookies</h3>
      <ul>
        <li><strong>session_id:</strong> Maintains your logged-in session</li>
        <li><strong>csrf_token:</strong> Protects against cross-site request forgery</li>
        <li><strong>auth_token:</strong> Authenticates API requests</li>
      </ul>

      <h3>Analytics Cookies</h3>
      <ul>
        <li><strong>_ga:</strong> Google Analytics user identifier</li>
        <li><strong>_gid:</strong> Google Analytics session identifier</li>
        <li><strong>_gat:</strong> Google Analytics request throttling</li>
      </ul>

      <h3>Functional Cookies</h3>
      <ul>
        <li><strong>preferences:</strong> Stores your display and layout preferences</li>
        <li><strong>recent_items:</strong> Remembers recently viewed claims and appeals</li>
      </ul>

      <h2>8. Updates to This Policy</h2>
      <p>
        We may update this Cookie Policy to reflect changes in our practices or for legal
        reasons. We will notify you of significant changes by posting the updated policy
        on our platform.
      </p>

      <h2>9. Contact Us</h2>
      <p>
        For questions about our use of cookies:
      </p>
      <ul>
        <li>Email: privacy@appealbot.com</li>
      </ul>
    </PolicyLayout>
  );
};

export default CookiePolicy;
